<template>
  <ckeditor
    class="editor"
    :editor="ClassicEditor"
    :model-value="editorData"
    :config="editorConfig"
    @ready="handleEditorReady"
    @input="handleUpdate"
    v-bind="$attrs"
  />
  <div class="char-counter">
    <span>{{ characterCount }} / {{ maxCharacters }} caracteres</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { message } from "ant-design-vue";

import {
  ClassicEditor,
  Essentials,
  Bold,
  Italic,
  Underline,
  Font,
  AutoLink,
  Link,
  Indent,
  List,
  TextTransformation,
  Alignment,
  Autoformat,
  Mention,
  Heading,
  // --- Suite completa de imágenes ---
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageTextAlternative,
  ImageUpload,
  ImageInsert,
  ImageResize,
  AutoImage,
  PictureEditing,
  LinkImage,
  GeneralHtmlSupport,
  SourceEditing,
  SimpleUploadAdapter,
  WordCount,
} from "ckeditor5";

defineOptions({
  inheritAttrs: false,
});

import "ckeditor5/ckeditor5.css";
import coreTranslations from "ckeditor5/translations/es.js";
import httpClient from "@/plugins/axios";
import { tokenManager } from "@/app/shared/utils/tokenManager";
import { normalizeEditorImageHtml } from "@/app/emails/utils/normalizeEditorImageHtml";

const buildUploadUrl = (path: string) => {
  const baseUrl = httpClient.defaults.baseURL || window.location.origin;
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

  return new URL(path, normalizedBase).toString();
};

const SIGNATURE_IMAGE_UPLOAD_URL = buildUploadUrl("mail/signatures/attachment/");
const SIGNATURE_IMAGE_VALIDATE_URL = buildUploadUrl("mail/signatures/validate-image-url/");
const accessToken = tokenManager.getToken();
const characterCount = ref(0);
let isReverting = false;

const props = defineProps({
  editorData: {
    type: String,
    default: "",
  },
  feed: {
    type: Array,
    default: () => [],
  },
  minimumCharactersFeed: {
    type: Number,
    default: 1,
  },
  maxCharacters: {
    type: Number,
    default: 10000,
  },
});

const emit = defineEmits(["update:editorData"]);

const MAX_IMAGE_COUNT = 3;
const MAX_IMAGE_SIZE_BYTES = 500 * 1024;
const IMAGE_UPLOAD_ENABLED_TOOLTIP = "Insertar imagen (máx. 3)";
const IMAGE_UPLOAD_DISABLED_TOOLTIP = "Alcanzaste el límite de 3 imágenes insertadas.";
const MAX_IMAGE_COUNT_MESSAGE = "No se insertaron las imágenes. Solo puedes insertar un máximo de 3.";
const MAX_IMAGE_SIZE_MESSAGE =
  "No se insertaron las imágenes por superar el límite de 500 KB. Redúcelas e inténtalo de nuevo.";
// Preserva el HTML insertado desde el modo fuente, pero bloquea etiquetas ejecutables y handlers inline.
const HTML_SUPPORT_ALLOW_RULES = [
  {
    name: /[\s\S]+/,
    attributes: true,
    classes: true,
    styles: true,
  },
];

const HTML_SUPPORT_DISALLOW_RULES = [
  {
    name: /^(script|iframe|object|embed|link|meta|base|style)$/i,
  },
  {
    attributes: {
      key: /^on.*$/i,
    },
  },
];

const SOURCE_EDITING_TOOLTIP = "Editar como HTML";
const SOURCE_EDITING_TRANSLATIONS = {
  es: {
    dictionary: {
      Source: SOURCE_EDITING_TOOLTIP,
      "Show source": SOURCE_EDITING_TOOLTIP,
    },
  },
};

const IMAGE_INSERT_TRANSLATIONS = {
  es: {
    dictionary: {
      "Insert image": IMAGE_UPLOAD_ENABLED_TOOLTIP,
      "Replace image": IMAGE_UPLOAD_ENABLED_TOOLTIP,
      "Upload image from computer": IMAGE_UPLOAD_ENABLED_TOOLTIP,
      "Replace image from computer": IMAGE_UPLOAD_ENABLED_TOOLTIP,
    },
  },
};

// Evita parchear el mismo plugin mas de una vez.
const patchedEditorPlugins = new WeakSet<object>();

const countImagesInEditor = (editor: any) => {
  const root = editor?.model?.document?.getRoot?.();

  if (!root) {
    return 0;
  }

  let count = 0;

  const visitNode = (node: any) => {
    if (typeof node?.getChildren !== "function") {
      return;
    }

    for (const child of node.getChildren()) {
      const isImageBlock = typeof child?.is === "function" && child.is("element", "imageBlock");
      const isImageInline = typeof child?.is === "function" && child.is("element", "imageInline");

      if (isImageBlock || isImageInline) {
        count += 1;
      }

      visitNode(child);
    }
  };

  visitNode(root);

  return count;
};

const getInsertImageDropdown = (editor: any) => {
  const toolbar = editor?.ui?.view?.toolbar;

  if (!toolbar?.items) {
    return null;
  }

  for (const item of toolbar.items) {
    if (item?.buttonView?.actionView?.iconView && item?.buttonView?.arrowView) {
      return item;
    }
  }

  return null;
};

const syncImageLimitState = (editor: any) => {
  const isImageLimitReached = countImagesInEditor(editor) >= MAX_IMAGE_COUNT;
  const insertImageDropdown = getInsertImageDropdown(editor);

  if (!insertImageDropdown) {
    return;
  }

  const tooltipText = isImageLimitReached
    ? IMAGE_UPLOAD_DISABLED_TOOLTIP
    : IMAGE_UPLOAD_ENABLED_TOOLTIP;

  const insertImageButton = insertImageDropdown.buttonView;

  insertImageDropdown.isEnabled = !isImageLimitReached;

  if (!insertImageButton) {
    return;
  }

  insertImageButton.label = tooltipText;
  insertImageButton.tooltip = true;

  const actionView = insertImageButton.actionView;

  if (!actionView) {
    return;
  }

  actionView.label = tooltipText;
  actionView.tooltip = true;
  actionView.isEnabled = !isImageLimitReached;

  const iconElement = actionView.iconView?.element;

  if (iconElement) {
    iconElement.classList.toggle("disabled", isImageLimitReached);
  }
};

// Envuelve el comando uploadImage de CKEditor para aplicar el límite antes de que la imagen entre al editor.
const patchUploadImageCommand = (editor: any) => {
  const uploadImageCommand = editor?.commands?.get?.("uploadImage");

  if (!uploadImageCommand || patchedEditorPlugins.has(uploadImageCommand)) {
    return;
  }

  patchedEditorPlugins.add(uploadImageCommand);

  const originalExecute = uploadImageCommand.execute.bind(uploadImageCommand);

  uploadImageCommand.execute = (options: { file?: File | File[] } = {}) => {
    const files = Array.isArray(options?.file)
      ? options.file
      : options?.file
        ? [options.file]
        : [];

    if (!files.length) {
      return originalExecute(options);
    }

    if (files.some((file) => file.size > MAX_IMAGE_SIZE_BYTES)) {
      message.error(MAX_IMAGE_SIZE_MESSAGE);
      return;
    }

    const remainingSlots = MAX_IMAGE_COUNT - countImagesInEditor(editor);

    if (remainingSlots <= 0) {
      message.error(MAX_IMAGE_COUNT_MESSAGE);
      return;
    }

    if (files.length > remainingSlots) {
      message.error(MAX_IMAGE_COUNT_MESSAGE);
    }

    const allowedFiles = files.slice(0, remainingSlots);

    return originalExecute({
      ...options,
      file: allowedFiles.length === 1 ? allowedFiles[0] : allowedFiles,
    });
  };
};

const handleEditorReady = (editor: any) => {
  // Intercepta warnings de upload para mostrarlos con Ant Design.
  const notificationPlugin = editor.plugins.get("Notification");

  if (notificationPlugin && !patchedEditorPlugins.has(notificationPlugin)) {
    patchedEditorPlugins.add(notificationPlugin);

    const originalShowWarning = notificationPlugin.showWarning.bind(notificationPlugin);

    // Si CKEditor avisa por subida de imagen, usamos message.error.
    notificationPlugin.showWarning = (warningMessage: any, data: { namespace?: string } = {}) => {
      if ((data?.namespace || "").startsWith("upload")) {
        message.error(
          typeof warningMessage === "string" ? warningMessage : "No se pudo subir la imagen."
        );
        return;
      }

      return originalShowWarning(warningMessage, data);
    };
  }

  patchUploadImageCommand(editor);

  // ImageInsertViaUrlUI controla el flujo de insertar imagen por URL.
  let imageInsertViaUrlUI: any = null;

  try {
    imageInsertViaUrlUI = editor.plugins.get("ImageInsertViaUrlUI");
  } catch {
    imageInsertViaUrlUI = null;
  }

  if (!imageInsertViaUrlUI || patchedEditorPlugins.has(imageInsertViaUrlUI)) {
    return;
  }

  patchedEditorPlugins.add(imageInsertViaUrlUI);

  // Guardamos la accion original para ejecutarla solo si la validacion pasa.
  const originalHandleSave = imageInsertViaUrlUI._handleSave.bind(imageInsertViaUrlUI);

  // Antes de insertar, validamos la URL en el backend.
  imageInsertViaUrlUI._handleSave = async () => {
    const imageUrl = (imageInsertViaUrlUI._formView?.imageURLInputValue || "").trim();
    const dialog = editor.plugins.get("Dialog");

    // Si no hay URL, dejamos que CKEditor siga su flujo normal.
    if (!imageUrl) {
      return originalHandleSave();
    }

    // Cerramos el modal apenas se confirma la accion para no dejarlo abierto
    dialog.hide();

    try {
      await httpClient.post(SIGNATURE_IMAGE_VALIDATE_URL, {
        url: imageUrl,
      });
    } catch (error) {
      // Mostramos el mensaje que devuelve el backend y detenemos la insercion.
      message.error(
        error?.response?.data?.error?.message || "No se pudo validar la imagen."
      );
      return;
    }

    // Si la URL es valida, CKEditor inserta la imagen como siempre.
    return originalHandleSave();
  };

  enforceCharacterLimit(editor);

  syncImageLimitState(editor);

  editor.model.document.on("change", () => {
    syncImageLimitState(editor);
  });
};

const handleUpdate = (newData: string) => {
  emit("update:editorData", newData);
};

const enforceCharacterLimit = (editor: any) => {
  const wordCountPlugin = editor.plugins.get("WordCount");

  // Escritura normal: si se excede, deshace el último caracter
  wordCountPlugin.on("update", (_evt: any, stats: { characters: number }) => {
    characterCount.value = stats.characters;

    if (isReverting) return;

    if (stats.characters <= props.maxCharacters) return;

    const overflow = stats.characters - props.maxCharacters;
    const selection = editor.model.document.selection;
    const endPosition = selection.getFirstPosition();

    if (!endPosition) return;

    isReverting = true;
    editor.model.change((writer: any) => {
      const startPosition = endPosition.getShiftedBy(-overflow);
      writer.remove(writer.createRange(startPosition, endPosition));
    });
    requestAnimationFrame(() => {
      isReverting = false;
    });
  });

  // Pegar: trunca el texto para que solo quepa lo que resta
  editor.editing.view.document.on(
    "clipboardInput",
    (evt: any, data: any) => {
      // Calcula cuántos caracteres libera la selección actual (si el usuario tiene texto seleccionado al pegar)
      const selection = editor.model.document.selection;
      let selectedChars = 0;

      for (const range of selection.getRanges()) {
        for (const item of range.getItems()) {
          if (item.is("$text") || item.is("$textProxy")) {
            selectedChars += item.data.length;
          }
        }
      }

      const remaining = props.maxCharacters - characterCount.value + selectedChars;

      // Sin espacio disponible: bloquea el pegado completamente
      if (remaining <= 0) {
        evt.stop();
        return;
      }

      const pastedText = data.dataTransfer.getData("text/plain");

      // El texto pegado cabe entero: flujo normal de CKEditor
      if (!pastedText || pastedText.length <= remaining) {
        return;
      }

      // Trunca y lo inserta manualmente
      evt.stop();

      const truncated = pastedText.slice(0, remaining);

      editor.model.change((writer: any) => {
        editor.model.insertContent(writer.createText(truncated), selection);
      });
    },
    { priority: "high" }
  );
};

const editorConfig = computed(() => ({
  plugins: [
    Essentials,
    Bold,
    Italic,
    Font,
    Link,
    Mention,
    Indent,
    List,
    TextTransformation,
    Alignment,
    Autoformat,
    AutoLink,
    Underline,
    Heading,
    SourceEditing,
    GeneralHtmlSupport,
    // --- Imágenes ---
    Image,
    ImageCaption,
    ImageStyle,
    ImageToolbar,
    ImageTextAlternative,
    ImageUpload,
    ImageInsert,
    ImageResize,
    AutoImage,
    PictureEditing,
    LinkImage,
    SimpleUploadAdapter,
    WordCount,
  ],
  simpleUpload: {
    uploadUrl: SIGNATURE_IMAGE_UPLOAD_URL,
    ...(accessToken
      ? {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      : {}),
  },
  placeholder: "Diseña tu firma aquí...", 
  language: "es", // <-- idioma de la interfaz (tooltips, menús, diálogos)
  translations: [coreTranslations, SOURCE_EDITING_TRANSLATIONS, IMAGE_INSERT_TRANSLATIONS], // <-- el set de strings en español
  toolbar: {
    items: [
      "heading",
      "bold",
      "underline",
      "italic",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "alignment",
      "numberedList",
      "bulletedList",
      "|",
      "link",
      "|",
      "insertImage", // dropdown unificado: subir archivo o pegar URL
      "|",
      "sourceEditing",
    ],
    shouldNotGroupWhenFull: true,
  },
  link: {
    addTargetToExternalLinks: true,
  },
  image: {
    insert: {
      integrations: ["upload", "url"], // quita "assetManager" porque eso es de CKBox
    },
    resizeUnit: "px",
    toolbar: [
      "imageStyle:alignBlockLeft",
      "imageStyle:block",
      "imageStyle:alignBlockRight",
      "|",
      "imageStyle:alignLeft",
      "imageStyle:inline",
      "imageStyle:alignRight",
      "|",
      "toggleImageCaption",
      "imageTextAlternative",
      "linkImage",
      "|",
      "resizeImage",
    ],
    resizeOptions: [
      { name: "resizeImage:original", value: null, label: "Original" },
      { name: "resizeImage:small", value: "96", label: "Pequeño" },
      { name: "resizeImage:medium", value: "200", label: "Mediano" },
      { name: "resizeImage:large", value: "420", label: "Grande" },
    ],
  },
  htmlSupport: {
    allow: HTML_SUPPORT_ALLOW_RULES,
    disallow: HTML_SUPPORT_DISALLOW_RULES,
    allowEmpty: ["span"],
  },
  mention: {
    feeds: [
      {
        marker: "#",
        minimumCharacters: props.minimumCharactersFeed,
        feed: props.feed,
      },
    ],
  },
  heading: {
    options: [
      {
        model: "paragraph",
        name: "Párrafo",
        title: "Párrafo",
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
    ],
  },
}));
</script>

<style>
:root {
  --ck-color-mention-background: white;
  --ck-color-mention-text: #fa8c16;
}

.ck.ck-editor__editable {
  min-height: 178px;
}

.ck-source-editing-area {
  min-height: 178px;
}

.ck.ck-editor__main {
  max-height: min(calc(100vh - 400px), 600px);
  overflow-y: auto;
}

.ck-source-editing-button .ck-button__label {
  display: none !important;
}

.ck.ck-dropdown.ck-heading-dropdown .ck-dropdown__button .ck-button__label {
  width: 4.5em;
}

.char-counter {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  text-align: right;
  margin-top: 4px;
}

</style>
